import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  TextField,
  CircularProgress,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { useDesignatedOperators, UseDesignatedOperatorsParams } from '../hooks/useDesignatedOperators';
import { useUser } from '../../../hooks/useUser';
import { UserRoles } from '../../../common/constants/userConstants';
import Modal from '../../../common/components/Modal/Modal';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import axios from 'axios';

// Add theme colors constant for consistent styling
const themeColors = {
  turq: '#00B6BA',
  navy: '#063142',
  light: '#F5F7FA'
};

interface DesignatedOperatorData {
  id: number;
  name: string;
  longName: string | null;
  disabled: boolean;
}

const DesignatedOperators: React.FC = () => {
  const { t } = useTranslation();
  
  // Get current user info
  const { data: userInfo, isLoading: userLoading } = useUser();
  
  // Check if user has admin role
  const isAdmin = userInfo?.role === UserRoles.ADMIN;
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Fetch designated operators with pagination
  const { data: operatorsData, isLoading, error, refetch } = useDesignatedOperators({
    page,
    size: rowsPerPage
  }, isAdmin);
  
  // State for modals and operator editing
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<DesignatedOperatorData | null>(null);
  const [newOperatorName, setNewOperatorName] = useState('');
  const [newOperatorLongName, setNewOperatorLongName] = useState('');
  const [editOperatorName, setEditOperatorName] = useState('');
  const [editOperatorLongName, setEditOperatorLongName] = useState('');
  const [editOperatorDisabled, setEditOperatorDisabled] = useState(false);
  
  // Alert state
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Validation state
  const [nameError, setNameError] = useState('');
  
  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: SelectChangeEvent<unknown>) => {
    const newSize = Number(event.target.value);
    setRowsPerPage(newSize);
    setPage(0);
  };
  
  // Handle modal close
  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setSelectedOperator(null);
    setNewOperatorName('');
    setNewOperatorLongName('');
    setEditOperatorName('');
    setEditOperatorLongName('');
    setEditOperatorDisabled(false);
    setNameError('');
  };
  
  // Handle alert close
  const handleAlertClose = () => {
    setAlert({...alert, open: false});
  };
  
  // Open edit modal
  const handleEditClick = (operator: DesignatedOperatorData) => {
    setSelectedOperator(operator);
    setEditOperatorName(operator.name);
    setEditOperatorLongName(operator.longName || '');
    setEditOperatorDisabled(operator.disabled);
    setIsEditModalOpen(true);
  };
  
  // Open delete modal
  const handleDeleteClick = (operator: DesignatedOperatorData) => {
    setSelectedOperator(operator);
    setIsDeleteModalOpen(true);
  };
  
  // Validate operator name
  const validateOperatorName = (name: string) => {
    if (!name.trim()) {
      setNameError('Operator name is required');
      return false;
    }
    setNameError('');
    return true;
  };
  
  // Add new operator
  const handleAddOperator = async () => {
    if (!validateOperatorName(newOperatorName)) return;
    
    try {
      // Using the correct request format for POST
      await axios.post('/designated-operators', {
        name: newOperatorName.trim(),
        longName: newOperatorLongName.trim() || ''
      });
      
      setAlert({
        open: true,
        message: 'Designated operator added successfully',
        severity: 'success'
      });
      
      refetch();
      handleCloseModal();
    } catch (error) {
      console.error('Error adding designated operator:', error);
      setAlert({
        open: true,
        message: 'Error adding designated operator',
        severity: 'error'
      });
    }
  };
  
  // Update operator
  const handleUpdateOperator = async () => {
    if (!selectedOperator) return;
    if (!validateOperatorName(editOperatorName)) return;
    
    try {
      // Using the correct request format for PUT
      await axios.put(`/designated-operators/${selectedOperator.id}`, {
        name: editOperatorName.trim(),
        longName: editOperatorLongName.trim() || '',
        disabled: editOperatorDisabled
      });
      
      setAlert({
        open: true,
        message: 'Designated operator updated successfully',
        severity: 'success'
      });
      
      refetch();
      handleCloseModal();
    } catch (error) {
      console.error('Error updating designated operator:', error);
      setAlert({
        open: true,
        message: 'Error updating designated operator',
        severity: 'error'
      });
    }
  };
  
  // Delete operator
  const handleDeleteOperator = async () => {
    if (!selectedOperator) return;
    
    try {
      await axios.delete(`/designated-operators/${selectedOperator.id}`);
      
      setAlert({
        open: true,
        message: 'Designated operator deleted successfully',
        severity: 'success'
      });
      
      refetch();
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting designated operator:', error);
      setAlert({
        open: true,
        message: 'Error deleting designated operator',
        severity: 'error'
      });
    }
  };
  
  // If not admin, show access denied
  if (!isAdmin) {
    return (
      <Paper elevation={0} sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          {t('common.accessDenied', 'Access Denied')}
        </Typography>
        <Typography variant="body1">
          {t('profile.designatedOperators.adminOnly', 'Only Admin can access this section.')}
        </Typography>
      </Paper>
    );
  }
  
  // Show loading state
  if (isLoading || userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">
          {t('common.error', 'An error occurred while loading designated operators.')}
        </Typography>
      </Box>
    );
  }

  // Get the designated operators from the paginated response
  const designatedOperators = operatorsData?.content || [];
  const totalCount = operatorsData?.totalElements || 0;
  
  return (
    <Paper elevation={0} sx={{ p: 2 }} data-testid="designated-operators-container">
      <Box sx={{ mb: 3, maxWidth: '50%' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {t('profile.designatedOperators.title', 'Designated Operators Management')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t('profile.designatedOperators.description', 'Manage the designated operators available in the system.')}
        </Typography>
      </Box>
      
      <Box sx={{ maxWidth: '50%' }}>
        <TableContainer>
          <Table 
            sx={{ 
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: '0 8px'
            }}
            aria-label="designated operators table"
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, border: 'none' }}>
                  {t('profile.designatedOperators.name', 'Name')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, border: 'none' }}>
                  {t('profile.designatedOperators.longName', 'Full Name')}
                </TableCell>
                <TableCell sx={{ fontWeight: 600, border: 'none' }}>
                  {t('profile.designatedOperators.status', 'Status')}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, border: 'none' }}>
                  {t('profile.designatedOperators.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {designatedOperators.map((operator) => (
                <TableRow 
                  key={operator.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: '#E7EFF5',
                    height: '34px',
                    '& td, & th': {
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      border: 'none'
                    }
                  }}
                  data-testid={`operator-row-${operator.id}`}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {operator.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {operator.longName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={operator.disabled ? 'Disabled' : 'Active'}
                      size="small"
                      sx={{ 
                        backgroundColor: operator.disabled ? '#FAAD14' : '#52C41A',
                        color: 'white',
                        padding: '2px 8px',
                        fontSize: '11px',
                        height: '22px',
                        fontWeight: '700',
                        borderRadius: '4px',
                        '& .MuiChip-label': {
                          padding: '0 4px'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleEditClick(operator)}
                        data-testid={`edit-operator-${operator.id}`}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteClick(operator)}
                        data-testid={`delete-operator-${operator.id}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {designatedOperators.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      {t('common.noResults', 'No operators found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Custom Pagination controls */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mt: 2,
          gap: 2
        }}>
          <FormControl 
            variant="outlined" 
            size="small" 
            sx={{ 
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                '&:hover fieldset': {
                  borderColor: themeColors.turq,
                },
              }
            }}
          >
            <InputLabel id="items-per-page-label" sx={{ color: themeColors.navy }}>
              {t('common.itemsPerPage', 'Items per page')}
            </InputLabel>
            <Select
              labelId="items-per-page-label"
              id="items-per-page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              label={t('common.itemsPerPage', 'Items per page')}
              data-testid="items-per-page-select"
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Button
              variant="outlined"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              sx={{ 
                minWidth: 40, 
                height: 40,
                p: '8px', 
                borderRadius: '8px',
                borderColor: themeColors.turq,
                color: themeColors.turq,
                '&:hover': {
                  borderColor: themeColors.turq,
                  backgroundColor: 'rgba(0, 182, 186, 0.1)',
                }
              }}
              data-testid="prev-page-button"
            >
              &lt;
            </Button>
            <Typography variant="body2" sx={{ mx: 1 }}>
              {page + 1} / {Math.max(1, Math.ceil(totalCount / rowsPerPage))}
            </Typography>
            <Button
              variant="outlined"
              disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
              sx={{ 
                minWidth: 40, 
                height: 40,
                p: '8px',
                borderRadius: '8px',
                borderColor: themeColors.turq,
                color: themeColors.turq,
                '&:hover': {
                  borderColor: themeColors.turq,
                  backgroundColor: 'rgba(0, 182, 186, 0.1)',
                }
              }}
              data-testid="next-page-button"
            >
              &gt;
            </Button>
          </Box>
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={() => setIsAddModalOpen(true)}
            data-testid="add-operator-button"
            sx={{ 
              textTransform: 'none',
              borderColor: themeColors.turq,
              color: themeColors.turq,
              '&:hover': {
                borderColor: themeColors.turq,
                backgroundColor: 'rgba(0, 182, 186, 0.1)',
              }
            }}
          >
            {t('profile.designatedOperators.addOperator', 'Add Designated Operator')}
          </Button>
        </Box>
      </Box>
      
      {/* Add Operator Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        title={t('profile.designatedOperators.addOperator', 'Add Designated Operator')}
        onApply={handleAddOperator}
        applyText={t('common.add', 'Add')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <TextField
            label={t('profile.designatedOperators.name', 'Name')}
            value={newOperatorName}
            onChange={(e) => {
              setNewOperatorName(e.target.value);
              if (e.target.value) setNameError('');
            }}
            fullWidth
            margin="normal"
            required
            error={!!nameError}
            helperText={nameError}
            data-testid="new-operator-name"
          />
          <TextField
            label={t('profile.designatedOperators.longName', 'Full Name')}
            value={newOperatorLongName}
            onChange={(e) => setNewOperatorLongName(e.target.value)}
            fullWidth
            margin="normal"
            data-testid="new-operator-long-name"
          />
        </Box>
      </Modal>
      
      {/* Edit Operator Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseModal}
        title={t('profile.designatedOperators.editOperator', 'Edit Designated Operator')}
        onApply={handleUpdateOperator}
        applyText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <TextField
            label={t('profile.designatedOperators.name', 'Name')}
            value={editOperatorName}
            onChange={(e) => {
              setEditOperatorName(e.target.value);
              if (e.target.value) setNameError('');
            }}
            fullWidth
            margin="normal"
            required
            error={!!nameError}
            helperText={nameError}
            data-testid="edit-operator-name"
          />
          <TextField
            label={t('profile.designatedOperators.longName', 'Full Name')}
            value={editOperatorLongName}
            onChange={(e) => setEditOperatorLongName(e.target.value)}
            fullWidth
            margin="normal"
            data-testid="edit-operator-long-name"
          />
          
          {/* Only show enable button for disabled operators */}
          {editOperatorDisabled && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('profile.designatedOperators.status', 'Status')}:
              </Typography>
              <Button
                variant="contained"
                color="success"
                onClick={() => setEditOperatorDisabled(false)}
                sx={{ textTransform: 'none' }}
                data-testid="enable-operator-button"
              >
                {t('common.enableOperator', 'Enable Operator')}
              </Button>
              <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                {t('profile.designatedOperators.currentlyDisabled', 'This operator is currently disabled.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        title={t('profile.designatedOperators.deleteOperator', 'Delete Designated Operator')}
        onApply={handleDeleteOperator}
        applyText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        maxWidth="sm"
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            {t('profile.designatedOperators.deleteConfirmation', 'Are you sure you want to delete this designated operator?')}
          </Typography>
          {selectedOperator && (
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
              {selectedOperator.name}
            </Typography>
          )}
        </Box>
      </Modal>
      
      {/* Status Alert */}
      <NotificationSnackbar
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleAlertClose}
        data-testid="designated-operators-notification"
      />
    </Paper>
  );
};

export default DesignatedOperators; 